import { useContext, useState } from 'react';
import { Control } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { maskWholeNumber, pluralize } from '@givebutter/util';
import { faBell, faBellSlash, faFlag } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createBulkAction } from 'api/bulkActions';
import { useContactTypeContext } from 'assets/js/pages/contacts/ContactTypeContext';
import { ContactsViewContext } from 'assets/js/pages/contacts/ContactsViewContext';
import * as DropdownMenu from 'components/dropdown-menu';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import * as Form from 'components/form';
import { TableContext } from 'components/table/TableContext';
import { BulkActionType } from 'constants/bulkActions';
import { ContactType } from 'constants/contacts';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { BulkAction } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

type NotificationSettingOption = 'subscribe' | 'unsubscribe' | 'none';
type NotificationSettingFieldName = 'email' | 'phone' | 'mail';
type NotificationSettingPermissionFieldName =
    | 'emailPermission'
    | 'phonePermission'
    | 'mailPermission';
type UpdateContactNotificationsFormValues = {
    [key in NotificationSettingFieldName]: NotificationSettingOption;
} & {
    [key in NotificationSettingPermissionFieldName]: boolean;
};

interface UpdateContactNotificationsDialogProps
    extends Omit<AlertDialog.AlertDialogRootProps, 'open' | 'children'> {}
const UpdateContactNotificationsDialog = ({
    onOpenChange,
    ...rest
}: UpdateContactNotificationsDialogProps) => {
    const contactType = useContactTypeContext();
    const filterKey = contactType === ContactType.Individual ? 'contacts' : 'companies';
    const { success, error } = useToast();
    const [isOpen, setIsOpen] = useState(false);
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();
    const { table, searchTerm } = useContext(TableContext);
    const { archivedInput } = useContext(ContactsViewContext);
    const isArchived = archivedInput === 'archived';
    const tableState = table.getState();
    const isAllSelected = tableState.isAllRecordsSelected;
    const selectedRowIds = Object.keys(tableState.rowSelection).map(Number);
    const contactIds: number[] = isAllSelected ? [] : selectedRowIds;
    const rowCount = isAllSelected ? table.getRowCount() : contactIds.length;
    const { filters } = useFiltersByKey(filterKey);

    const methods = Form.useGivebutterForm<UpdateContactNotificationsFormValues>({
        defaultValues: {
            email: 'none',
            phone: 'none',
            mail: 'none',
            emailPermission: false,
            phonePermission: false,
            mailPermission: false,
        },
    });
    const {
        control,
        handleSubmit,
        watch,
        reset,
        resetFormButton,
        formState: { isDirty, isValid },
    } = methods;
    const [email, phone, mail] = watch(['email', 'phone', 'mail']);

    const { mutate: submit, isPending } = useMutation<
        BulkAction,
        GivebutterAxiosError,
        UpdateContactNotificationsFormValues
    >({
        mutationFn: values => {
            const { email, phone, mail } = values;
            return createBulkAction(accountId, {
                type: BulkActionType.MANAGE_CONTACT_ENGAGE_SUBSCRIPTIONS,
                filters: isAllSelected
                    ? {
                          query: searchTerm,
                          type: contactType,
                          archiveOnly: isArchived,
                          ...filters,
                      }
                    : undefined,
                data: {
                    ids: isAllSelected ? undefined : contactIds,
                    is_email_subscribed: email === 'none' ? null : email === 'subscribe',
                    is_phone_subscribed: phone === 'none' ? null : phone === 'subscribe',
                    is_address_subscribed: mail === 'none' ? null : mail === 'subscribe',
                },
            });
        },
        onSuccess: () => {
            success('Contact Engage Subscription settings updated.');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.contacts(accountId) });
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.bulkActions(accountId) });
            setIsOpen(false);
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message || 'Something went wrong. Please try again.');
            resetFormButton();
        },
    });

    return (
        <AlertDialog.Root
            open={isOpen}
            onOpenChange={isOpen => {
                setIsOpen(isOpen);
                onOpenChange?.(isOpen);
                reset();
            }}
            {...rest}
        >
            <AlertDialog.Trigger asChild onClick={() => setIsOpen(true)}>
                <DropdownMenu.Item onSelect={e => e.preventDefault()}>
                    Manage subscriptions
                </DropdownMenu.Item>
            </AlertDialog.Trigger>
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <Form.Provider {...methods}>
                    <form onSubmit={handleSubmit(values => submit(values))}>
                        <AlertDialog.Content className="max-w-lg">
                            <AlertDialog.Header>
                                <AlertDialog.Close />
                                <AlertDialog.Icon />
                            </AlertDialog.Header>
                            <AlertDialog.Body className="flex flex-col gap-4">
                                <AlertDialog.Title>Subscriptions</AlertDialog.Title>
                                <AlertDialog.Description>
                                    These settings are for <strong>Engage</strong> messages only,
                                    and will not affect system messages such as email receipts. This
                                    will apply to all selected contacts.
                                </AlertDialog.Description>
                                <AlertNotification.Root type={isAllSelected ? 'warning' : 'info'}>
                                    <AlertNotification.Icon />
                                    <AlertNotification.Content>
                                        <AlertNotification.Title>
                                            You are managing subscription status for{' '}
                                            {maskWholeNumber(rowCount)}{' '}
                                            {pluralize(rowCount, 'contact')}
                                            {isAllSelected ? ' across multiple pages.' : '.'}
                                        </AlertNotification.Title>
                                        <AlertNotification.Description>
                                            Ensure you have properly selected the contacts you would
                                            like to edit before continuing.
                                        </AlertNotification.Description>
                                    </AlertNotification.Content>
                                </AlertNotification.Root>

                                <Form.Container className="gap-6">
                                    <div className="flex flex-col gap-4">
                                        <div>
                                            <Form.Label name="email">Email</Form.Label>
                                            <SubscriptionSettingSelect
                                                control={control}
                                                name="email"
                                            />
                                        </div>
                                        {email === 'subscribe' && (
                                            <SubscriptionSettingConfirmation
                                                control={control}
                                                label="Email"
                                                name="emailPermission"
                                                parentSettingName="email"
                                            />
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-4">
                                        <div>
                                            <Form.Label name="phone">SMS</Form.Label>
                                            <SubscriptionSettingSelect
                                                control={control}
                                                name="phone"
                                            />
                                        </div>
                                        {phone === 'subscribe' && (
                                            <SubscriptionSettingConfirmation
                                                control={control}
                                                label="SMS"
                                                name="phonePermission"
                                                parentSettingName="phone"
                                            />
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-4">
                                        <div>
                                            <Form.Label name="phone">Mail</Form.Label>
                                            <SubscriptionSettingSelect
                                                control={control}
                                                name="mail"
                                            />
                                        </div>
                                        {mail === 'subscribe' && (
                                            <SubscriptionSettingConfirmation
                                                control={control}
                                                label="Mail"
                                                name="mailPermission"
                                                parentSettingName="mail"
                                            />
                                        )}
                                    </div>
                                </Form.Container>
                            </AlertDialog.Body>
                            <AlertDialog.Footer>
                                <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                                <Form.SubmitButton
                                    className="flex-1"
                                    isSubmitting={isPending}
                                    disabled={!isDirty || !isValid}
                                >
                                    Save
                                </Form.SubmitButton>
                            </AlertDialog.Footer>
                        </AlertDialog.Content>
                    </form>
                </Form.Provider>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const SubscriptionSettingSelect = ({
    control,
    name,
}: {
    control: Control<UpdateContactNotificationsFormValues, any>;
    name: NotificationSettingFieldName;
}) => {
    return (
        <Form.SingleSelect control={control} name={name}>
            <SingleSelect.Trigger aria-label="Subscription setting" />
            <SingleSelect.Content>
                <SingleSelect.Item value="none">
                    <FontAwesomeIcon icon={faFlag} />
                    <SingleSelect.ItemText>Keep current preference</SingleSelect.ItemText>
                </SingleSelect.Item>
                <SingleSelect.Item value="subscribe">
                    <FontAwesomeIcon icon={faBell} />
                    <SingleSelect.ItemText>Subscribe</SingleSelect.ItemText>
                </SingleSelect.Item>
                <SingleSelect.Item value="unsubscribe">
                    <FontAwesomeIcon icon={faBellSlash} />
                    <SingleSelect.ItemText>Unsubscribe</SingleSelect.ItemText>
                </SingleSelect.Item>
            </SingleSelect.Content>
        </Form.SingleSelect>
    );
};

const SubscriptionSettingConfirmation = ({
    control,
    name,
    parentSettingName,
    label,
}: {
    control: Control<UpdateContactNotificationsFormValues, any>;
    label: 'SMS' | 'Email' | 'Mail';
    name: NotificationSettingPermissionFieldName;
    parentSettingName: NotificationSettingFieldName;
}) => {
    return (
        <AlertNotification.Root type="info" closable={false}>
            <AlertNotification.Icon />
            <AlertNotification.Content>
                <AlertNotification.Title>
                    {parentSettingName === 'mail' ? 'Letters' : `Messages`} will be sent their
                    primary {parentSettingName === 'mail' ? 'address' : parentSettingName}.
                </AlertNotification.Title>
                <AlertNotification.Description>
                    You are about to subscribe the selected contacts to receive{' '}
                    {parentSettingName === 'mail' ? 'letters' : `${label} messages`} from Engage. To
                    proceed, please confirm they have given you permission to do so.
                </AlertNotification.Description>
                <div className="flex items-center gap-2">
                    <Form.UseFormCheckboxInput
                        name={name}
                        control={control}
                        rules={{
                            validate: (value, formValues) => {
                                return (
                                    formValues[parentSettingName] === 'subscribe' && value === true
                                );
                            },
                        }}
                    />
                    <Form.UseFormLabel name={name} className="cursor-pointer text-gray-700">
                        I confirm they have given me permission.
                    </Form.UseFormLabel>
                </div>
            </AlertNotification.Content>
        </AlertNotification.Root>
    );
};

export { UpdateContactNotificationsDialog };
